<template>
  <div class="lead">
    <div class="divider"></div>
    <section>
      <h2>Customer Information</h2>
      <p @click="navigateToAddress(leadAddress)" class="address-link">
        <span v-if="leadAddress.LINE1">{{ leadAddress.LINE1 }}<br /></span>
        <span v-if="leadAddress.LINE2">{{ leadAddress.LINE2 }}<br /></span>
        <span v-if="leadAddress.CITY">{{ leadAddress.CITY }}, </span>
        <span v-if="leadAddress.STATE">{{ leadAddress.STATE }}</span>
        <span v-if="leadAddress.POSTALCODE">{{ ' ' + leadAddress.POSTALCODE }}</span>
      </p>
      <p>
        <span v-for="phone in leadPhones">{{ formatPhone(phone.NUMBERDISPLAY) }} {{ getPhoneType(phone.TYPEID) }}<br /></span>
        <span v-if="leadEmail" @click="navigateToEmail(leadEmail.ADDRESS)" class="email-link">{{ leadEmail.ADDRESS }}</span>
      </p>
    </section>
    <div class="divider"></div>
    <section>
      <div class="edit-icon" @click="showEditDetails = true">
        <img id="noHover" src="../assets/pencil.svg" />
        <img id="hover" src="../assets/pencil-hover.svg" />
      </div>
      <h2>Request Details</h2>
      <div class="info">
        <div class="info-block">
          <label>Job Name</label>
          <p>{{ lead.JOBNAME || '-' }}</p>
        </div>
        <div class="info-block">
          <label>BW Quote</label>
          <p>{{ lead.BWQUOTE || '-' }}</p>
        </div>
        <div class="info-block" v-if="!lead.COMPLETE">
          <label>Amount</label>
          <p>{{ lead.AMOUNT ? '$' + lead.AMOUNT : '-' }}</p>
        </div>
        <div class="info-block" v-if="!lead.HASDELIVERY">
          <label>Delivery</label>
          <p>No</p>
        </div>
        <div class="info-block" v-if="lead.COMPLETE">
          <label>Complete Date</label>
          <p>{{ formatDate(lead.COMPLETEDATE) }}</p>
        </div>
        <div class="info-block" v-if="lead.COMPLETE">
          <label>Invoice Number</label>
          <p>{{ lead.INVNUM || '-' }}</p>
        </div>
        <div class="info-block" v-if="lead.COMPLETE">
          <label>Amount</label>
          <p>{{ lead.AMOUNT ? '$' + lead.AMOUNT : '-' }}</p>
        </div>
        <div class="info-block full-width">
          <label>Notes</label>
          <p>{{ lead.NOTES || '-' }}</p>
        </div>
        <div class="info-block" v-if="lead.HASDELIVERY">
          <label>Delivery</label>
          <p>Yes</p>
        </div>
        <div class="info-block" v-if="lead.HASDELIVERY">
          <label>Site Address</label>
          <p>{{ lead.DELIVERYADDRESS || '-' }}</p>
        </div>
        <div class="info-block" v-if="lead.HASDELIVERY">
          <label>Delivery Date</label>
          <p>{{ formatDate(lead.DELIVERYDATE) }}</p>
        </div>
        <div class="info-block" v-if="lead.HASDELIVERY">
          <label>Gate Code</label>
          <p>{{ lead.GATECODE || '-' }}</p>
        </div>
        <div class="info-block full-width" v-if="lead.HASDELIVERY">
          <label>Delivery Notes</label>
          <p>{{ lead.DELIVERYNOTES || '-' }}</p>
        </div>
      </div>
    </section>
    <div class="divider" v-if="lead.QUOTESUMMARY"></div>
    <section v-if="lead.QUOTESUMMARY">
      <h2>Quote Request Summary</h2>
      <div class="info">
        <div :class="{ 'info-block': true, 'full-width': summaryDetail.keyName == 'additionalDetails' }" v-for="summaryDetail in sortedQuoteSummary">
          <label>{{ summaryDetail.name }}</label>
          <p>{{ summaryDetail.value ? summaryDetail.value.toString().charAt(0).toUpperCase() + summaryDetail.value.toString().slice(1) : '' }}</p>
        </div>
      </div>
    </section>
    <div class="divider" v-if="lead.QUOTEDETAILS"></div>
    <section v-if="lead.QUOTEDETAILS" class="table-section">
      <div class="section-header">
        <h2>Quote Request Details</h2>
      </div>
      <div class="lead-table quote-details-table">
        <div class="table-row">
          <div class="table-header">Type</div>
          <div class="table-header">Product</div>
          <div class="table-header">Surface</div>
          <div class="table-header">Color</div>
          <div class="table-header">Size</div>
        </div>
        <div class="table-row" v-for="row in formattedQuoteDetails" :key="row.type">
          <div class="table-cell">{{ row.type }}</div>
          <div class="table-cell">{{ row.product }}</div>
          <div class="table-cell">{{ row.surface }}</div>
          <div class="table-cell">{{ row.color }}</div>
          <div class="table-cell">{{ row.size }}</div>
        </div>
      </div>
    </section>
    <div class="divider"></div>
    <section class="table-section">
      <div class="section-header">
        <h2>Notes and Interactions</h2>
        <button @click="showAddInteraction = true" class="add-square-btn-small"><img src="../assets/plus-symbol.svg" />Add</button>
      </div>
      <div class="lead-table interactions-table">
        <div class="table-row">
          <div class="table-header">Type</div>
          <div class="table-header">Date</div>
          <div class="table-header">Sales Person</div>
          <div class="table-header">Files</div>
          <div class="table-header">Note</div>
          <div class="table-header"></div>
        </div>
        <div class="table-row" v-for="interaction in sortedInteractions">
          <div class="table-cell">{{ interaction.TYPE }}</div>
          <div class="table-cell">{{ formatDate(interaction.DATE) }}</div>
          <div class="table-cell">{{ findSalesperson(interaction.SALESPERSONID) }}</div>
          <div class="table-cell interaction-files">
            <span v-for="file in interactionFiles[interaction.INTERACTIONID]" @click.stop="openFolder(interaction.INTERACTIONID + '/' + file.name)">
              <img v-if="checkFileType(file.type) == 'image'" src="../assets/filetype-image.svg" />
              <img v-else-if="checkFileType(file.type) == 'pdf'" src="../assets/filetype-pdf.svg" />
              <img v-else-if="checkFileType(file.type) == 'word'" src="../assets/filetype-word.svg" />
              <img v-else-if="checkFileType(file.type) == 'excel'" src="../assets/filetype-excel.svg" />
              <img v-else-if="checkFileType(file.type) == 'email'" src="../assets/filetype-email.svg" />
              <img v-else src="../assets/filetype-other.svg" alt="" />{{ file.name }}
            </span>
            <span v-if="interactionFiles[interaction.INTERACTIONID]?.length > 1" @click.stop="showEditInteraction = interaction.INTERACTIONID" class="interaction-files-showmore"
              >+{{ interactionFiles[interaction.INTERACTIONID].length - 1 }}</span
            >
          </div>
          <div class="table-cell interaction-note">{{ interaction.NOTE }}</div>
          <div
            class="table-cell context-menu"
            tabindex="0"
            @click="showContextMenu[interaction.INTERACTIONID] = true"
            :class="{ open: showContextMenu[interaction.INTERACTIONID] }"
            @focusout="showContextMenu[interaction.INTERACTIONID] = false">
            <img id="noHover" src="../assets/three-dots-menu.svg" />
            <img id="hover" src="../assets/three-dots-menu-hover.svg" />
            <div class="context-dropdown">
              <div
                @click.stop="
                  showEditInteraction = interaction.INTERACTIONID;
                  showContextMenu[interaction.INTERACTIONID] = false;
                ">
                <img src="../assets/pencil-dark.svg" />View
              </div>
              <div
                v-if="interaction.SALESPERSONID == user.userid || user.leadsAdmin"
                @click.stop="
                  showDeleteInteractionConfirm = interaction.INTERACTIONID;
                  showContextMenu[interaction.INTERACTIONID] = false;
                ">
                <img src="../assets/trash.svg" />Delete
              </div>
            </div>
          </div>
        </div>
        <div class="table-row" v-if="!sortedInteractions.length">
          <div style="width: 100%" class="table-cell"><i>There are no interactions for this lead.</i></div>
        </div>
      </div>
    </section>
    <div class="divider"></div>
    <section class="table-section">
      <div class="section-header">
        <h2>Files</h2>
        <div class="actions">
          <button @click="$refs.fileUpload.$refs.fileUploadInput.click()" class="add-square-btn-small"><img src="../assets/plus-symbol.svg" />Add</button>
        </div>
      </div>
      <FileUpload :leadid="leadid" :isPanel="false" ref="fileUpload" />
      <div class="lead-table files-table">
        <div class="table-row">
          <div class="table-header">Type</div>
          <div class="table-header">Name</div>
          <div class="table-header">Date</div>
          <div class="table-header"></div>
          <div class="table-header"></div>
        </div>
        <div class="table-row" v-for="file in leadOnlyFiles">
          <div class="table-cell file-image" @click="getFile(leadid, file.name)">
            <img v-if="checkFileType(file.type) == 'image'" src="../assets/filetype-image.svg" />
            <img v-else-if="checkFileType(file.type) == 'pdf'" src="../assets/filetype-pdf.svg" />
            <img v-else-if="checkFileType(file.type) == 'word'" src="../assets/filetype-word.svg" />
            <img v-else-if="checkFileType(file.type) == 'excel'" src="../assets/filetype-excel.svg" />
            <img v-else-if="checkFileType(file.type) == 'email'" src="../assets/filetype-email.svg" />
            <img v-else src="../assets/filetype-other.svg" alt="" />
          </div>
          <div class="table-cell file-name" @click="getFile(leadid, file.name)">{{ formatFileName(file.name) }}</div>
          <div class="table-cell">{{ getFormatedDate(file.date) }}</div>
          <div class="table-cell"></div>
          <div class="table-cell context-menu" tabindex="0" @click="showContextMenu[file.name] = true" :class="{ open: showContextMenu[file.name] }" @focusout="showContextMenu[file.name] = false">
            <img id="noHover" src="../assets/three-dots-menu.svg" />
            <img id="hover" src="../assets/three-dots-menu-hover.svg" />
            <div class="context-dropdown">
              <div
                @click.stop="
                  showRenameFile = file.name;
                  showContextMenu[file.name] = false;
                ">
                <img src="../assets/pencil-dark.svg" />Rename
              </div>
              <div
                @click.stop="
                  showDeleteFileConfirm = file.name;
                  showContextMenu[file.name] = false;
                ">
                <img src="../assets/trash.svg" />Delete
              </div>
            </div>
          </div>
        </div>
        <div class="table-row" v-if="!filesData.length && !loadingFiles && !filesError">
          <div style="width: 100%" class="table-cell"><i>There are no files for this lead.</i></div>
        </div>
        <div class="table-row loading-row" v-if="loadingFiles">
          <div style="width: 100%" class="table-cell"><img src="../assets/spinner.svg" /></div>
        </div>
        <div class="table-row" v-if="filesError">
          <div style="width: 100%" class="table-cell"><i>Error fetching files. Please try again later.</i></div>
        </div>
      </div>
    </section>
    <div class="divider" v-if="sortedHistory.length || historyError"></div>
    <section class="table-section" v-if="sortedHistory.length || historyError">
      <div class="section-header">
        <h2>Previous Requests</h2>
      </div>
      <div class="lead-table history-table">
        <div class="table-row">
          <div class="table-header">Date</div>
          <div class="table-header">Source</div>
          <div class="table-header">Job Name</div>
          <div class="table-header">Amount</div>
          <div class="table-header">Notes</div>
        </div>
        <div class="table-row" v-for="historicalLead in sortedHistory" @click="openHistoricalLead(historicalLead.LEADID)">
          <div class="table-cell">{{ formatDate(historicalLead.CREATEDATE) }}</div>
          <div class="table-cell">{{ historicalLead.SOURCE }}</div>
          <div class="table-cell">{{ historicalLead.JOBNAME }}</div>
          <div class="table-cell">{{ historicalLead.AMOUNT ? '$' + historicalLead.AMOUNT : '-' }}</div>
          <div class="table-cell">{{ historicalLead.NOTES }}</div>
        </div>
        <div class="table-row loading-row" v-if="loadingHistory">
          <div style="width: 100%" class="table-cell"><img src="../assets/spinner.svg" /></div>
        </div>
        <div class="table-row" v-if="historyError">
          <div style="width: 100%" class="table-cell"><i>Error fetching history. Please try again later.</i></div>
        </div>
      </div>
    </section>
    <div @click="$emit('closeLead')" class="bottom-spacer"></div>
  </div>
  <AddInteraction v-if="showAddInteraction" :leadid="leadid" @closePopup="showAddInteraction = false" />
  <EditInteraction v-if="showEditInteraction" :leadid="leadid" :interactionid="showEditInteraction" @closePopup="showEditInteraction = null" :files="interactionFiles[showEditInteraction]" />
  <Confirmation
    v-if="showDeleteInteractionConfirm"
    @confirm="handleDeleteInteraction(showDeleteInteractionConfirm)"
    @cancel="showDeleteInteractionConfirm = null"
    header="Delete Interaction"
    message="Are you sure you want to delete this interaction? This action cannot be undone."
    confirmText="Delete"
    cancelText="Cancel"
    :serious="true" />
  <RenameFile v-if="showRenameFile" :filename="showRenameFile" :leadid="leadid" @closePopup="showRenameFile = null" />
  <Confirmation
    v-if="showDeleteFileConfirm"
    @confirm="handleDeleteFile(showDeleteFileConfirm)"
    @cancel="showDeleteFileConfirm = null"
    header="Delete File"
    message="Are you sure you want to delete this file? This action cannot be undone."
    confirmText="Delete"
    cancelText="Cancel"
    :serious="true" />
  <EditRequestDetails v-if="showEditDetails" :leadid="leadid" @closePopup="showEditDetails = false" />
</template>

<script>
import useLeadsQuery from '@/composables/useLeadsQuery';
import useUsersQuery from '@/composables/useUsersQuery';
import useInteractionMutate from '@/composables/useInteractionMutate';
import AddInteraction from '@/components/AddInteraction.vue';
import EditInteraction from '@/components/EditInteraction.vue';
import Confirmation from '../components/Confirmation.vue';
import useAlerts from '@/composables/useAlerts';
import EditRequestDetails from './EditRequestDetails.vue';
import useCurrentUser from '@/composables/useCurrentUser';
import FileUpload from './FileUpload.vue';
import useLeadFiles from '@/composables/useLeadFiles';
import RenameFile from './RenameFile.vue';
import useSingleLeadQuery from '@/composables/useSingleLeadQuery';
import useLeadFilesQuery from '@/composables/useLeadFilesQuery';
import useLeadHistoryQuery from '@/composables/useLeadHistoryQuery';
import { ref, computed, watch } from 'vue';

export default {
  name: 'Lead',
  props: ['leadid'],
  emits: ['closeLead'],
  components: {
    AddInteraction,
    EditInteraction,
    Confirmation,
    EditRequestDetails,
    FileUpload,
    RenameFile,
  },
  setup(props) {
    const { deleteInteraction } = useInteractionMutate();
    const { addAlert } = useAlerts();
    const { user } = useCurrentUser();
    const { deleteFile, getFile } = useLeadFiles();
    const { showSingleLead } = useSingleLeadQuery;
    const { allUsers } = useUsersQuery();

    // Initialize file and history queries
    const { fetchLeadFiles, getLeadFiles, refetchLeadFiles } = useLeadFilesQuery();
    const { fetchLeadHistory, getLeadHistory, refetchLeadHistory } = useLeadHistoryQuery();

    // Reactive references to current lead's data
    const currentLeadId = ref(props.leadid);

    // Computed properties for current lead's files and history
    const filesData = computed(() => getLeadFiles(currentLeadId.value).data);
    const loadingFiles = computed(() => getLeadFiles(currentLeadId.value).loading);
    const filesError = computed(() => getLeadFiles(currentLeadId.value).error);

    const historyData = computed(() => getLeadHistory(currentLeadId.value).data);
    const loadingHistory = computed(() => getLeadHistory(currentLeadId.value).loading);
    const historyError = computed(() => getLeadHistory(currentLeadId.value).error);

    // Fetch files and history when leadid changes
    watch(
      () => props.leadid,
      async (newLeadId) => {
        if (newLeadId) {
          currentLeadId.value = newLeadId;
          fetchLeadFiles(newLeadId);
          fetchLeadHistory(newLeadId);
        }
      },
      { immediate: true }
    );

    return {
      leads: useLeadsQuery.leadsIncludingSingleLead,
      loadingLeads: useLeadsQuery.loadingLeads,
      allUsers,
      showSingleLead,
      deleteInteraction,
      addAlert,
      user,
      deleteFile,
      filesData,
      historyData,
      loadingFiles,
      loadingHistory,
      filesError,
      historyError,
      refetchLeadFiles,
      refetchLeadHistory,
      getFile,
    };
  },
  data() {
    return {
      showAddInteraction: false,
      showEditInteraction: null,
      showContextMenu: {},
      showDeleteInteractionConfirm: null,
      showDeleteFileConfirm: null,
      showRenameFile: null,
      showEditDetails: false,
      dragging: false,
    };
  },
  computed: {
    lead() {
      return this.leads.find((lead) => lead.LEADID == this.leadid) || {};
    },
    leadAddress() {
      if (this.lead.CONTACTID) {
        return this.lead.contact?.address || {};
      } else if (this.lead.COMPANYID) {
        return this.lead.company?.address || {};
      } else {
        return this.lead.address || {};
      }
    },
    leadPhones() {
      let phones = [];
      if (this.lead.CONTACTID) {
        phones = this.lead.contact?.phones || [];
      } else if (this.lead.COMPANYID) {
        phones = this.lead.company?.phones || [];
      } else {
        phones = this.lead.phones || [];
      }
      const removeBlanks = phones.filter((phone) => phone.NUMBERDISPLAY);
      const removeDuplicates = removeBlanks.filter((phone, index, self) => index === self.findIndex((t) => t.NUMBERDISPLAY === phone.NUMBERDISPLAY && this.getPhoneType(t.TYPEID) === this.getPhoneType(phone.TYPEID)));
      return removeDuplicates;
    },
    leadEmail() {
      if (this.lead.CONTACTID) {
        return this.lead.contact?.email;
      } else if (this.lead.email) {
        return this.lead.email || {};
      }
    },
    sortedInteractions() {
      return this.lead.interactions?.sort((a, b) => b.DATE - a.DATE) || [];
    },
    sortedHistory() {
      if (!this.historyData || this.historyData.length === 0) return [];
      return [...this.historyData].sort((a, b) => b.CREATEDATE - a.CREATEDATE);
    },
    sortedQuoteSummary() {
      if (!this.lead?.QUOTESUMMARY) return [];
      return Object.entries(this.lead.QUOTESUMMARY)
        .map(([key, value]) => ({ ...value, keyName: key }))
        .sort((a, b) => a.index - b.index);
    },
    formattedQuoteDetails() {
      const details = [];
      const quoteDetails = this.lead.QUOTEDETAILS;

      // Wall
      if (quoteDetails?.wall) {
        const wall = quoteDetails.wall;
        const pattern = wall.wall_pattern?.value && wall.wall_pattern.value !== 'single-stone' ? ` - ${wall.wall_pattern.value}` : '';
        const size = wall.wall_stone_size ? ` (${wall.wall_stone_size.value})` : '';
        details.push({
          type: 'Wall',
          product: `${wall.wall_product?.value || ''}${pattern}${size}`,
          surface: wall.wall_surface?.value || '',
          color: wall.wall_color?.value || '',
          size: `${wall.wall_size_length?.value || ''}' x ${wall.wall_size_height?.value || ''}' (${wall.wall_size_combine_total?.value || ''} ft²)`,
        });
      }

      // Column
      if (quoteDetails?.column) {
        const column = quoteDetails.column;
        const pattern = column.column_pattern?.value && column.column_pattern.value !== 'single-stone' ? ` - ${column.column_pattern.value}` : '';
        const size = column.column_stone_size ? ` (${column.column_stone_size.value})` : '';
        details.push({
          type: 'Column',
          product: `${column.column_product?.value || ''}${pattern}${size}`,
          surface: column.column_surface?.value || '',
          color: column.column_color?.value || '',
          size: `${column.column_size_length?.value || ''}' x ${column.column_size_width?.value || ''}' x ${column.column_size_height?.value || ''}' (${column.column_size_number?.value || ''} columns)`,
        });
      }

      // Patio
      if (quoteDetails?.patio) {
        const patio = quoteDetails.patio;
        const filterPatternString = patio.patio_pattern?.value?.replace('single-stone, ', '')?.replace(', single-stone', '')?.replace('single-stone', '') || '';
        const pattern = filterPatternString ? ' - ' + filterPatternString : '';
        const size = patio.patio_stone_size ? (pattern ? `, ${patio.patio_stone_size.value}` : ` - ${patio.patio_stone_size.value}`) : '';
        let sizeDisplay = '';
        if (patio.patio_shape?.value === 'circle') {
          const circleType = patio.patio_size_circle_type?.value || '';
          const circleTypeText = circleType ? ` (${circleType})` : '';
          sizeDisplay = `${patio.patio_size_circle_diameter?.value || ''}' diameter${circleTypeText} (${patio.patio_size_combine_total?.value || ''} ft²)`;
        } else if (patio.patio_shape?.value === 'triangle') {
          sizeDisplay = `${patio.patio_size_triangle_width?.value || ''}' x ${patio.patio_size_triangle_length?.value || ''}' (${patio.patio_size_combine_total?.value || ''} ft²)`;
        } else {
          sizeDisplay = `${patio.patio_size_rec_width?.value || ''}' x ${patio.patio_size_rec_length?.value || ''}' (${patio.patio_size_combine_total?.value || ''} ft²)`;
        }
        details.push({
          type: 'Patio',
          product: `${patio.patio_product?.value || ''}${pattern}${size}`,
          surface: patio.patio_surface?.value || '',
          color: patio.patio_color?.value || '',
          size: sizeDisplay,
        });
      }

      // Patio Border
      if (quoteDetails?.patioBorder?.patio_border_included?.value === 'yes') {
        const border = quoteDetails.patioBorder;
        details.push({
          type: 'Patio Border',
          product: `${border.patio_border_product?.value || ''}${border.patio_border_stone_size ? ' (' + border.patio_border_stone_size.value + ')' : ''}`,
          surface: border.patio_border_surface?.value || '',
          color: border.patio_border_color?.value || '',
          size: border.patio_border_stone_pattern?.value ? border.patio_border_stone_pattern.value.charAt(0).toUpperCase() + border.patio_border_stone_pattern.value.slice(1) : '',
        });
      }

      // Driveway
      if (quoteDetails?.driveway) {
        const driveway = quoteDetails.driveway;
        const filterPatternString = driveway.driveway_pattern?.value?.replace('single-stone, ', '').replace(', single-stone', '')?.replace('single-stone', '') || '';
        const pattern = filterPatternString ? ' - ' + filterPatternString : '';
        const size = driveway.driveway_stone_size ? (pattern ? `, ${driveway.driveway_stone_size.value}` : ` - ${driveway.driveway_stone_size.value}`) : '';
        let sizeDisplay = '';
        if (driveway.driveway_shape?.value === 'circle') {
          const circleType = driveway.driveway_size_circle_type?.value || '';
          const circleTypeText = circleType ? ` (${circleType})` : '';
          sizeDisplay = `${driveway.driveway_size_circle_diameter?.value || ''}' diameter${circleTypeText} (${driveway.driveway_size_combine_total?.value || ''} ft²)`;
        } else if (driveway.driveway_shape?.value === 'triangle') {
          sizeDisplay = `${driveway.driveway_size_triangle_width?.value || ''}' x ${driveway.driveway_size_triangle_length?.value || ''}' (${driveway.driveway_size_combine_total?.value || ''} ft²)`;
        } else {
          sizeDisplay = `${driveway.driveway_size_rec_width?.value || ''}' x ${driveway.driveway_size_rec_length?.value || ''}' (${driveway.driveway_size_combine_total?.value || ''} ft²)`;
        }
        details.push({
          type: 'Driveway',
          product: `${driveway.driveway_product?.value || ''}${pattern}${size}`,
          surface: driveway.driveway_surface?.value || '',
          color: driveway.driveway_color?.value || '',
          size: sizeDisplay,
        });
      }

      // Driveway Border
      if (quoteDetails?.drivewayBorder?.driveway_border_included?.value === 'yes') {
        const border = quoteDetails.drivewayBorder;
        details.push({
          type: 'Driveway Border',
          product: `${border.driveway_border_product?.value || ''}${border.driveway_border_stone_size ? ' (' + border.driveway_border_stone_size.value + ')' : ''}`,
          surface: border.driveway_border_surface?.value || '',
          color: border.driveway_border_color?.value || '',
          size: border.driveway_border_stone_pattern?.value ? border.driveway_border_stone_pattern.value.charAt(0).toUpperCase() + border.driveway_border_stone_pattern.value.slice(1) : '',
        });
      }

      // Fire Feature
      if (quoteDetails?.fireFeature) {
        const fire = quoteDetails.fireFeature;
        details.push({
          type: 'Fire Feature',
          product: fire.fire_feature_product?.value || '',
          surface: fire.fire_feature_surface?.value || '',
          color: fire.fire_feature_color?.value || '',
          size: '-',
        });
      }

      return details;
    },
    leadOnlyFiles() {
      return this.filesData.filter((file) => !file.interactionid);
    },
    interactionFiles() {
      const interactionFilesMap = {};

      if (this.lead && Array.isArray(this.lead.interactions)) {
        this.lead.interactions.forEach((interaction) => {
          interactionFilesMap[interaction.INTERACTIONID] = this.filesData.filter((file) => file.interactionid.toLowerCase() == interaction.INTERACTIONID.toLowerCase());
        });
      }
      return interactionFilesMap;
    },
  },
  methods: {
    formatPhone(num) {
      const numbers = num?.replace(/\D/g, '') || '';
      const char = { 0: '(', 3: ') ', 6: '-' };
      let newValue = '';
      for (let i = 0; i < numbers.length; i++) {
        newValue += (char[i] || '') + numbers[i];
      }
      return newValue;
    },
    getPhoneType(typeid) {
      if (typeid == '511DF006-2CDB-40B5-8D1A-B313C873DD1C') return '(Cell)';
      else if (typeid == '2870C5A5-7B3B-49DE-AD57-763EA3BF41D0') return '(Fax)';
      else if (typeid == '17057633-4B31-4AE8-BE33-ED27C7FD1B12') return '(Toll Free)';
      else return '';
    },
    formatDate(date) {
      const dateInt = parseInt(date);
      if (isNaN(dateInt)) return '-';
      return new Date(dateInt).toLocaleDateString();
    },
    findSalesperson(salespersonId) {
      return this.allUsers.find((salesperson) => salesperson.USERID == salespersonId)?.DISPLAYNAME || '-';
    },
    async handleDeleteInteraction(interactionId) {
      this.showDeleteInteractionConfirm = null;

      try {
        await this.deleteInteraction(interactionId);
      } catch (error) {
        console.error(error);
      }

      this.addAlert('Deleted Interaction', 'low', 3);
      const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
      lead.interactions = lead.interactions.filter((interaction) => interaction.INTERACTIONID !== interactionId);
    },
    async handleDeleteFile(fileName) {
      this.showDeleteFileConfirm = null;

      try {
        await this.deleteFile(this.leadid, fileName);
      } catch (error) {
        console.error(error);
      }

      this.addAlert('Deleted File', 'low', 3);
      this.filesData = this.filesData.filter((file) => file.name !== fileName);
    },
    getFormatedDate(date) {
      date = new Date(parseInt(date));
      return date ? `${(date.getMonth() + 1).toString()}/${date.getDate().toString()}/${date.getFullYear()}` : null;
    },
    checkFileType(path) {
      const extension = path.split('.').pop().toLowerCase();
      switch (extension) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'image';
        case 'pdf':
          return 'pdf';
        case 'doc':
        case 'docx':
          return 'word';
        case 'xls':
        case 'xlsx':
          return 'excel';
        case 'eml':
        case 'msg':
          return 'email';
        default:
          return 'other';
      }
    },
    formatFileName(name) {
      if (!name.includes('.')) return name;
      const extension = name.split('.').pop().toLowerCase();
      return name.replace(`.${extension}`, '') + `.${extension}`;
    },
    navigateToAddress(address) {
      let addressString = '';
      if (address.LINE1) addressString += address.LINE1 + ', ';
      if (address.LINE2) addressString += address.LINE2 + ', ';
      if (address.CITY) addressString += address.CITY + ', ';
      if (address.STATE) addressString += address.STATE + ' ';
      if (address.POSTALCODE) addressString += address.POSTALCODE;

      window.open(`https://www.google.com/maps/search/${addressString}`, '_blank');
    },
    navigateToEmail(email) {
      window.open(`mailto:${email}`, '_blank');
    },
    openHistoricalLead(leadId) {
      const shouldClose = !this.showSingleLead;
      this.showSingleLead = leadId;
      if (shouldClose) this.$emit('closeLead');
    },
  },
};
</script>

<style scoped lang="scss">
.email-link,
.address-link {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
